import { useEffect, useState } from "react";
import styled from "styled-components";
import { styled as muiStyled } from "@mui/material/styles";
import { useSnackbar } from "notistack";

import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";

import CloseIcon from "@mui/icons-material/Close";

import { updateShippingOrder } from "src/api/shippingOrder";

import font from "src/styles/font";
import device from "src/styles/device";
import { Black, White01 } from "src/styles/color";

import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";

const WrapperContainer = styled.div`
  width: 100%;
`;

const CloseButton = styled(IconButton)`
  &.MuiIconButton-root {
    position: absolute;
    top: 16px;
    right: 16px;
    color: ${Black};
  }
`;

const WrapperBox = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 16px;

  /* disabled */
  .MuiInputBase-root.Mui-disabled {
    background-color: ${White01};
  }
`;

const SpanLabel = styled.div`
  font-size: ${font.size.md};
  font-weight: 400;
  line-height: 24px;

  @media ${device.mobileL} {
    font-size: ${font.size.sm};
  }
`;

const StyledRadioGroup = styled(RadioGroup)`
  .MuiFormControlLabel-label {
    width: 100%;
    font-size: ${font.size.md};
    font-weight: 400;
    line-height: 24px;
  }
`;

const StyledDialog = muiStyled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 16,
  },
  "& .MuiDialogContent-root": {
    maxHeight: "500px",
    border: 0,
    padding: 24,
    paddingTop: 0,
  },
  "& .MuiDialog-container": {
    "& .MuiPaper-root": {
      width: "100%",
      maxWidth: 640,
    },
  },
  "& .MuiDialogActions-root": {
    justifyContent: "center",
  },
  "& .MuiDialogActions-root>:not(:first-of-type)": {
    marginLeft: 24,
  },

  [theme.breakpoints.down("sm")]: {
    "& .MuiPaper-root": {
      margin: 24,
    },
    "& .MuiDialogContent-root": {
      padding: 16,
      paddingTop: 0,
    },
    "& .MuiDialogActions-root": {
      padding: 16,
    },
  },
}));

const DeliveryFailedDialog = ({
  onClose,
  onSuccess,
  orderDetail,
  setIsLoading,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const initialDetail = {
    order: null,
    method: "ส่งไปถึงลูกค้าโดยตรง",
    fulfillmentProcess: [
      {
        type: "มีค่าจัดส่ง",
        carrier: "จัดส่งโดยร้านค้า",
        trackingNumber: null,
        trackingUrl: null,
        shippingFee: null,
        pickupAddress: {
          name: null,
          tel: null,
          address: null,
          coordinates: {
            lat: null,
            lng: null,
          },
        },
        shippingProofs: [],
        deliveryProofs: [],
        shippedDate: null,
        estimatedDeliveryDate: null,
        actualDeliveryDate: null,
        remark: null,
      },
    ],
    payment: {
      method: null,
      info: {
        bankAccountName: null,
        bankNo: null,
      },
      total: null,
      paymentDate: null,
      paymentProof: null,
    },
    status: {
      failureReason: null,
      value: "pending",
    },
    note: null,
    documents: {
      customer: {
        qt: {
          qtDate: null,
          qtNo: null,
          customerQtFile: null,
          customerQtAdditionalFile: null,
        },
        inv: {
          invDate: null,
          invNo: null,
          customerInvFile: null,
          customerInvAdditionalFile: null,
        },
      },
      xsurface: {
        po: {
          poDate: null,
          poNo: null,
          xsurfacePoFile: null,
          xsurfacePoAdditionalFile: null,
        },
        vc: {
          vcDate: null,
          vcNo: null,
          xsurfaceVcFile: null,
          xsurfaceVcAdditionalFile: null,
        },
      },
      seller: {
        qt: {
          qtDate: null,
          qtNo: null,
          sellerQtFile: null,
          sellerQtAdditionalFile: null,
        },
        inv: {
          invDate: null,
          invNo: null,
          sellerInvFile: null,
          sellerInvAdditionalFile: null,
        },
      },
    },
  };

  const [shippingDetail, setShippingDetail] = useState(initialDetail);

  const isJSONString = (str) => {
    if (typeof str !== "string") return str;
    try {
      return JSON.parse(str);
    } catch {
      return str;
    }
  };

  useEffect(() => {
    if (!!orderDetail) {
      setShippingDetail((order) => ({
        ...order,
        ...orderDetail,
        method: orderDetail.method
          ? isJSONString(orderDetail.method)
          : "ส่งไปถึงลูกค้าโดยตรง",
        fulfillmentProcess:
          orderDetail.fulfillmentProcess.length > 0
            ? orderDetail.fulfillmentProcess
            : initialDetail.fulfillmentProcess,
        payment: orderDetail.payment,
        documents: orderDetail.documents,
        status: {
          failureReason: null,
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetail]);

  const cancelReasons = [
    "สินค้าไม่พร้อมส่ง",
    "ลูกค้าไม่รับสินค้า",
    "ติดต่อลูกค้าไม่ได้",
    "ร้านค้าใช้เวลาเตรียมสินค้านานเกินไป",
    "ลูกค้าเปลี่ยนใจ",
    "ยกเลิกการจัดส่ง",
    "อื่นๆ",
  ];

  const [isOtherReason, setIsOtherReason] = useState(false);

  const [updateOrder, setUpdateOrder] = useState({
    cancelReason: "", // ??
  });

  const handleSelectReason = ({ target }) => {
    if (target.value === "อื่นๆ") {
      setIsOtherReason(true);
      setUpdateOrder({
        ...updateOrder,
        cancelReason: "",
      });
    } else {
      setIsOtherReason(false);
      setUpdateOrder({
        ...updateOrder,
        cancelReason: target.value,
      });
    }
  };

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      const payload = {
        status: {
          value: "failed",
          failureReason: shippingDetail.status.failureReason,
        },
        order: orderDetail?.order?._id,
      };
      delete payload.orderDetail;

      const data = new FormData();
      for (const field in payload) {
        if (
          field === "order" ||
          field === "_id" ||
          field === "method" ||
          field === "note" ||
          field === "seller"
        ) {
          data.append(field, payload[field]);
        } else {
          data.append(field, JSON.stringify(payload[field]));
        }
      }

      delete data.documents;

      await updateShippingOrder(shippingDetail._id, data);
      await onSuccess();
      enqueueSnackbar(`แก้ไขสถานะเป็นจัดส่งไม่สำเร็จ เรียบร้อยแล้ว`, {
        variant: "success",
      });
    } catch (err) {
      console.error("err:", err.response);
      enqueueSnackbar(err.response.data.message, {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateField = () => {
    if (shippingDetail?.status?.failureReason) return false;
    else return true;
  };

  return (
    <StyledDialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
    >
      <DialogTitle
        sx={{
          p: {
            sm: 3,
            xs: 2,
          },
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontSize: {
            sm: font.size.lg,
            xs: font.size.md,
          },
          fontWeight: 600,
        }}
      >
        สาเหตุที่ส่งไม่สำเร็จ
        <CloseButton aria-label="close" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </CloseButton>
      </DialogTitle>

      <DialogContent>
        <WrapperContainer>
          <FormControl sx={{ width: "100%" }}>
            <StyledRadioGroup
              aria-labelledby="cancel-reason-radio-buttons-group-label"
              name="cancel-reason-radio-buttons-group"
              value={updateOrder?.cancelReason}
              onChange={handleSelectReason}
            >
              {cancelReasons.map((option) => (
                <FormControlLabel
                  value={option}
                  checked={shippingDetail?.status?.failureReason}
                  control={
                    <Radio
                      checked={
                        option === "อื่นๆ"
                          ? isOtherReason
                          : updateOrder?.cancelReason === option
                      }
                      sx={{
                        color: "#e04132",
                        "&.Mui-checked": {
                          color: "#e04132",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 22,
                        },
                      }}
                    />
                  }
                  onChange={({ target }) => {
                    const newValue = shippingDetail;
                    newValue.status.failureReason = target.value;
                    setShippingDetail({ ...newValue });
                  }}
                  label={
                    <>
                      {option === "อื่นๆ" ? (
                        <WrapperBox>
                          <SpanLabel>{option}</SpanLabel>

                          <TextField
                            variant="outlined"
                            fullWidth
                            onChange={({ target }) => {
                              const newValue = shippingDetail;
                              newValue.status.failureReason = target.value;
                              setShippingDetail({ ...newValue });
                            }}
                            value={
                              isOtherReason
                                ? shippingDetail?.status.failureReason
                                : ""
                            }
                            disabled={!isOtherReason}
                            inputProps={{
                              sx: {
                                padding: {
                                  sm: "12px 16px",
                                  xs: "8px 12px",
                                },
                              },
                            }}
                            sx={{
                              maxWidth: {
                                sm: 200,
                                xs: "unset",
                              },
                            }}
                          />
                        </WrapperBox>
                      ) : (
                        <SpanLabel>{option}</SpanLabel>
                      )}
                    </>
                  }
                />
              ))}
            </StyledRadioGroup>
          </FormControl>
        </WrapperContainer>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="outlined"
          color="secondary"
          size="large"
          fullWidth
          onClick={() => onClose()}
          sx={{ maxWidth: 180 }}
        >
          ยกเลิก
        </Button>

        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={() => onSubmit()}
          sx={{ maxWidth: 180 }}
          disabled={validateField()}
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default DeliveryFailedDialog;
